import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, MapPin, Trophy } from 'lucide-react';
import proTournamentInfoService, { ProTournamentInfo, isHubPage } from '../../services/proTournamentInfoService';
import { buildFileUrl } from './ProTournamentsSection';
import { formatDateRange, nextSessionLabel } from './HomePdcCalendar';
import { t as tr } from '../../i18n';

const ACCENT = '#3fced1';

/**
 * Laufende Turniere für die „Jetzt live“-Karte über den News (Marcel 2026-09-17):
 * nur Status LIVE (der Service liefert bereits den effektiven Status), ältester
 * Start zuerst – das Hauptevent vor einem parallel laufenden Dachturnier.
 * Pure Funktion, testbar ohne Rendering.
 */
export const liveTournamentsForLead = (tournaments: ProTournamentInfo[]): ProTournamentInfo[] =>
  tournaments
    .filter((t) => t && t.slug && t.status === 'LIVE' && !isHubPage(t))
    .sort((a, b) => (a.startDate ?? 0) - (b.startDate ?? 0));

const LiveLeadCard: React.FC<{ tournament: ProTournamentInfo }> = ({ tournament: t }) => {
  const [imgError, setImgError] = useState(false);
  // Breitformat zuerst: Hero-Banner vor Thumbnail vor Logo
  const image = buildFileUrl(t.heroImage || t.thumbnail || t.logo);
  const session = nextSessionLabel(t);
  const location = [t.venue, t.city].filter(Boolean).join(', ');

  return (
    <Link to={`/turniere/${t.slug}`} className="group block">
      {/* Bild ohne Overlay/Text (fest 16:9), Inhalt im Card-Body: Mobile darunter,
          Desktop rechts neben dem Bild (Marcel 2026-09-17) */}
      <article className="flex flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition-colors hover:border-[#3fced1]/50 lg:flex-row">
        <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-zinc-800 lg:w-[46%]">
          {image && !imgError ? (
            <img
              src={image}
              alt={t.name || tr('web.component.home-live-pdc-lead.pdc-turnier')}
              loading="eager"
              decoding="async"
              onError={() => setImgError(true)}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 via-zinc-900 to-black">
              <Trophy size={48} className="text-zinc-700" />
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center p-4 sm:p-5">
          <span className="mb-2 inline-flex items-center gap-1.5 self-start rounded bg-red-500/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
            {tr('web.component.matches-section.live')}
          </span>
          <h2 className="line-clamp-2 text-xl font-extrabold leading-tight tracking-tight text-white transition-colors group-hover:text-[#3fced1] sm:text-2xl">
            {t.name || t.shortName || tr('web.component.home-live-pdc-lead.pdc-turnier')}
          </h2>
          {session && <p className="mt-1.5 text-sm font-semibold text-gray-200">{session}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 sm:text-sm">
            <span className="flex items-center gap-1.5">
              <CalendarDays size={13} style={{ color: ACCENT }} />
              {formatDateRange(t)}
            </span>
            {location && (
              <span className="flex min-w-0 items-center gap-1.5">
                <MapPin size={13} className="shrink-0" />
                <span className="truncate">{location}</span>
              </span>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
};

/**
 * „Heute“: laufende PDC-Turniere als große Karte im Stil der News-Leitkarte,
 * direkt ÜBER der News-Sektion (Marcel 2026-09-17). Rendert nichts, wenn kein
 * Turnier LIVE ist. Klick → /turniere/:slug, Header-Link → PDC-Kalender.
 */
const HomeLivePdcLead: React.FC = () => {
  const [live, setLive] = useState<ProTournamentInfo[]>([]);

  useEffect(() => {
    let cancelled = false;
    proTournamentInfoService
      .list({ limit: 50 })
      .then((list) => {
        if (!cancelled) setLive(liveTournamentsForLead(list));
      })
      .catch(() => {
        /* Sektion bleibt ausgeblendet */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (live.length === 0) return null;

  return (
    <section aria-label={tr('web.component.home-live-pdc-lead.heute')} className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">{tr('web.component.home-live-pdc-lead.heute')}</h2>
        <Link
          to="/pdc-turniere"
          className="flex items-center gap-1 text-[13px] font-semibold transition-opacity hover:opacity-80"
          style={{ color: ACCENT }}
        >
          {tr('web.component.home-live-pdc-lead.pdc-kalender')}
          <ArrowRight size={14} />
        </Link>
      </div>
      <div className="space-y-4">
        {live.map((t) => (
          <LiveLeadCard key={t.slug || t.id} tournament={t} />
        ))}
      </div>
    </section>
  );
};

export default HomeLivePdcLead;
